import { useEffect, useMemo, useState } from 'react';
import useDebounce from 'src/hooks/useDebounce';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import useIsDuplicateInvoiceNumberApi from 'src/services/useIsDuplicateInvoiceNumberApi';

const useInvoiceNumberWarnings = (
  invoice: InvoiceDetailsResponse,
  initialInvoiceNumber: string
) => {
  const [hasDuplicateInvoiceNumber, setHasDuplicateInvoiceNumber] = useState(false);
  const { execute: validateInvoiceNumber, isValidatingDuplicateInvoiceNumber } =
    useIsDuplicateInvoiceNumberApi(invoice?.receiptId?.toString().trim());
  const debouncedInvoiceNumber = useDebounce(invoice?.invoiceNumber?.toString().trim());

  useEffect(() => {
    if (debouncedInvoiceNumber) {
      if (debouncedInvoiceNumber === initialInvoiceNumber) {
        setHasDuplicateInvoiceNumber(invoice.isInvoiceNumberDuplicate);
      } else {
        validateInvoiceNumber({ invoiceNumber: debouncedInvoiceNumber }).then(
          (hasDuplicateInvoiceNumber) => {
            setHasDuplicateInvoiceNumber(hasDuplicateInvoiceNumber);
          }
        );
      }
    }
  }, [
    debouncedInvoiceNumber,
    initialInvoiceNumber,
    invoice.isInvoiceNumberDuplicate,
    validateInvoiceNumber,
  ]);

  const info = useMemo(
    () =>
      hasDuplicateInvoiceNumber && debouncedInvoiceNumber
        ? ['Document Number is already used on another document.']
        : [],
    [debouncedInvoiceNumber, hasDuplicateInvoiceNumber]
  );

  const errors: string[] = useMemo(
    () =>
      !invoice?.invoiceNumber?.toString().trim()
        ? ['Document number is empty.']
        : invoice?.invoiceNumber?.toString().trim().length > 75
        ? ['Document Number is more than 75 characters.']
        : [],
    [invoice?.invoiceNumber]
  );

  return { info, errors, hasDuplicateInvoiceNumber, isValidatingDuplicateInvoiceNumber };
};

export default useInvoiceNumberWarnings;
